import registry from "../data/staticSeoRegistry.json";

export const STATIC_SEO_SITE_URL = "https://www.vibe2nite.com";

/**
 * @typedef {"home"|"product"|"destination_index"|"destination_guide"|"venue_directory"|"category_landing"|"event_listing"|"useful_service"|"awards_index"|"award_detail"|"legal"} StaticSeoPageType
 * @typedef {"static_omit_lastmod"|"live_data_omit_lastmod"} StaticSeoFreshnessPolicy
 * @typedef {Object} StaticSeoPage
 * @property {string} routeId
 * @property {string} path
 * @property {StaticSeoPageType} pageType
 * @property {boolean} indexable
 * @property {boolean} prerender
 * @property {boolean} sitemap
 * @property {string} canonicalPath
 * @property {string} title
 * @property {string} description
 * @property {"WebPage"|"CollectionPage"} schemaType
 * @property {StaticSeoFreshnessPolicy} freshnessPolicy
 */

/** @type {Readonly<Record<string, StaticSeoPage>>} */
export const STATIC_SEO_REGISTRY = Object.freeze(registry);

export function normalizeStaticSeoPath(value) {
  const raw = String(value || "/").split(/[?#]/, 1)[0] || "/";
  const withLeadingSlash = raw.startsWith("/") ? raw : `/${raw}`;
  return withLeadingSlash === "/" ? "/" : withLeadingSlash.replace(/\/+$/, "");
}

/** @returns {StaticSeoPage | null} */
export function getStaticSeoPage(pathname) {
  return STATIC_SEO_REGISTRY[normalizeStaticSeoPath(pathname)] || null;
}

/** @returns {StaticSeoPage[]} */
export function getStaticSeoPages() {
  return Object.values(STATIC_SEO_REGISTRY);
}

export function staticSeoCanonicalUrl(pageOrPath) {
  const page =
    typeof pageOrPath === "string" ? getStaticSeoPage(pageOrPath) : pageOrPath;
  if (!page?.canonicalPath) return null;
  return `${STATIC_SEO_SITE_URL}${page.canonicalPath === "/" ? "/" : page.canonicalPath}`;
}

function upsertMeta(doc, selector, attributeName, attributeValue, content) {
  let tag = doc.head.querySelector(selector);
  if (!tag) {
    tag = doc.createElement("meta");
    tag.setAttribute(attributeName, attributeValue);
    doc.head.appendChild(tag);
  }
  tag.setAttribute("content", content);
}

export function applyStaticSeoMetadata(pathname, doc = document) {
  const page = getStaticSeoPage(pathname);
  if (!page || !doc?.head) return null;

  const canonicalUrl = staticSeoCanonicalUrl(page);
  doc.title = page.title;

  doc.querySelectorAll('link[rel="canonical"]').forEach((tag, index) => {
    if (index > 0) tag.remove();
  });
  let canonical = doc.head.querySelector('link[rel="canonical"]');
  if (!canonical) {
    canonical = doc.createElement("link");
    canonical.setAttribute("rel", "canonical");
    doc.head.appendChild(canonical);
  }
  canonical.setAttribute("href", canonicalUrl);

  upsertMeta(doc, 'meta[name="description"]', "name", "description", page.description);
  upsertMeta(doc, 'meta[property="og:title"]', "property", "og:title", page.title);
  upsertMeta(
    doc,
    'meta[property="og:description"]',
    "property",
    "og:description",
    page.description
  );
  upsertMeta(doc, 'meta[property="og:url"]', "property", "og:url", canonicalUrl);
  upsertMeta(doc, 'meta[name="twitter:title"]', "name", "twitter:title", page.title);
  upsertMeta(
    doc,
    'meta[name="twitter:description"]',
    "name",
    "twitter:description",
    page.description
  );

  return page;
}
